import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import HrLayout from "../HrLayout";

const PAGE_SIZE = 10;

export default function HrReports() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000)
    .toISOString()
    .slice(0, 10);

  const [range, setRange] = useState("month");
  const [summary, setSummary] = useState(null);

  const [start, setStart] = useState(monthAgo);
  const [end, setEnd] = useState(today);

  const [tab, setTab] = useState("attendance"); // attendance | leaves
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const loadSummary = async () => {
    const res = await api.get("/hr/reports/summary", { params: { range } });
    setSummary(res.data);
    setStart(res.data.start);
    setEnd(res.data.end);
  };

  const loadRows = async () => {
    setLoading(true);
    setError("");
    try {
      const url =
        tab === "attendance"
          ? "/hr/reports/attendance"
          : "/hr/reports/leaves";

      const res = await api.get(url, { params: { start, end } });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setError("Failed to load reports data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadSummary();
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [range]);

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line
  }, [tab, start, end]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((r) => {
      const name = `${r.first_name || ""} ${r.last_name || ""}`.toLowerCase();
      const dept = (r.department_name || "").toLowerCase();
      const code = (r.employee_code || "").toLowerCase();
      const type = (r.leave_type || "").toLowerCase();
      return (
        name.includes(query) ||
        dept.includes(query) ||
        code.includes(query) ||
        type.includes(query)
      );
    });
  }, [rows, q]);

  const totalPages = Math.max(Math.ceil(filtered.length / PAGE_SIZE), 1);

  const pageData = useMemo(() => {
    const startIdx = (page - 1) * PAGE_SIZE;
    return filtered.slice(startIdx, startIdx + PAGE_SIZE);
  }, [filtered, page]);

  useEffect(() => setPage(1), [q, tab, start, end]);

  const exportCSV = () => {
    const headers =
      tab === "attendance"
        ? ["Date", "Employee", "Code", "Department", "Status", "CheckIn", "CheckOut", "WorkHours"]
        : ["Created", "Employee", "Code", "Department", "Type", "From", "To", "Status"];

    const lines = [headers.join(",")];

    pageData.forEach((r) => {
      if (tab === "attendance") {
        lines.push(
          [
            r.date,
            `"${r.first_name} ${r.last_name}"`,
            r.employee_code,
            `"${r.department_name || ""}"`,
            r.status,
            r.check_in || "",
            r.check_out || "",
            r.work_hours || ""
          ].join(",")
        );
      } else {
        lines.push(
          [
            r.created_at,
            `"${r.first_name} ${r.last_name}"`,
            r.employee_code,
            `"${r.department_name || ""}"`,
            `"${r.leave_type || ""}"`,
            r.start_date,
            r.end_date,
            r.status
          ].join(",")
        );
      }
    });

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `hr_${tab}_report_${start}_to_${end}.csv`;
    link.click();
  };

  return (
    <HrLayout title="Reports">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">HR Reports</h1>
          <p className="text-sm text-slate-500 mt-1">
            Attendance & Leave reports with filters and export.
          </p>
        </div>

        <button
          onClick={exportCSV}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
        >
          Export CSV (Current Page)
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card title="Employees" value={summary.totalEmployees} />
          <Card title="Attendance Records" value={summary.totalAttendanceRecords} />
          <Card title="Leaves (Range)" value={summary.totalLeaves} />
          <Card title="Pending Leaves" value={summary.pendingLeaves} />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-3">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Range preset
            </label>
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="week">Last 7 days</option>
              <option value="month">Last 30 days</option>
              <option value="quarter">Last 90 days</option>
            </select>
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Start
            </label>
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              End
            </label>
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Search
            </label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="name / code / dept / type"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-4 flex gap-2">
          <TabBtn active={tab === "attendance"} onClick={() => setTab("attendance")}>
            Attendance
          </TabBtn>
          <TabBtn active={tab === "leaves"} onClick={() => setTab("leaves")}>
            Leaves
          </TabBtn>

          <button
            onClick={loadRows}
            className="ml-auto px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          {tab === "attendance" ? (
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-slate-100 border-b border-slate-200">
                <tr>
                  <Th>Date</Th>
                  <Th>Employee</Th>
                  <Th>Dept</Th>
                  <Th>Status</Th>
                  <Th>Check-In</Th>
                  <Th>Check-Out</Th>
                  <Th>Work</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><Td colSpan={7} className="text-center py-10 text-slate-500">Loading…</Td></tr>
                ) : pageData.length === 0 ? (
                  <tr><Td colSpan={7} className="text-center py-10 text-slate-500">No data</Td></tr>
                ) : (
                  pageData.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <Td>{r.date}</Td>
                      <Td className="font-semibold">{r.first_name} {r.last_name}</Td>
                      <Td>{r.department_name || "—"}</Td>
                      <Td><Chip>{r.status}</Chip></Td>
                      <Td>{r.check_in || "—"}</Td>
                      <Td>{r.check_out || "—"}</Td>
                      <Td>{r.work_hours ? `${r.work_hours} min` : "—"}</Td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-slate-100 border-b border-slate-200">
                <tr>
                  <Th>Created</Th>
                  <Th>Employee</Th>
                  <Th>Dept</Th>
                  <Th>Type</Th>
                  <Th>From</Th>
                  <Th>To</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><Td colSpan={7} className="text-center py-10 text-slate-500">Loading…</Td></tr>
                ) : pageData.length === 0 ? (
                  <tr><Td colSpan={7} className="text-center py-10 text-slate-500">No data</Td></tr>
                ) : (
                  pageData.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <Td>{String(r.created_at).slice(0, 10)}</Td>
                      <Td className="font-semibold">{r.first_name} {r.last_name}</Td>
                      <Td>{r.department_name || "—"}</Td>
                      <Td>{r.leave_type}</Td>
                      <Td>{r.start_date}</Td>
                      <Td>{r.end_date}</Td>
                      <Td><Chip>{r.status}</Chip></Td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && filtered.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-xs text-slate-500">
              Showing <b className="text-slate-700">{(page - 1) * PAGE_SIZE + 1}</b> to{" "}
              <b className="text-slate-700">{Math.min(page * PAGE_SIZE, filtered.length)}</b> of{" "}
              <b className="text-slate-700">{filtered.length}</b>
            </p>

            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold disabled:opacity-50 hover:bg-slate-50"
              >
                Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold disabled:opacity-50 hover:bg-slate-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </HrLayout>
  );
}

/* UI */
function Card({ title, value }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
        {title}
      </p>
      <p className="mt-2 text-2xl font-bold text-slate-800">{value ?? 0}</p>
    </div>
  );
}
function TabBtn({ active, children, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
        active
          ? "bg-indigo-600 text-white border-indigo-600"
          : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}
function Th({ children }) {
  return (
    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500">
      {children}
    </th>
  );
}
function Td({ children, colSpan, className = "" }) {
  return (
    <td colSpan={colSpan} className={`px-4 py-3 text-slate-700 ${className}`}>
      {children}
    </td>
  );
}
function Chip({ children }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-xs font-semibold text-slate-700">
      {String(children || "").toUpperCase()}
    </span>
  );
}
